import { Global } from '@emotion/react';
import { createInstance, HackleProvider } from '@hackler/react-sdk';
import { ModalsProvider, NotificationsProvider, ThemeProvider } from '@inflearn/ds-react-next';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import Cookies from 'js-cookie';
import type { ReactNode } from 'react';

import { CDN_URL, HACKLE_BROWSER_SDK_KEY } from './appConfig';
import globals from './styles/globals';

import '@inflearn/ds-react-next/mantine-core-styles.css';
import '@inflearn/ds-react-next/mantine-notifications-styles.css';
// Mantine v8은 패키지별로 CSS가 분리되어 있고, ds-react-next는 @mantine/dates에 의존하지
// 않으므로 core CSS에 날짜 관련 스타일이 포함되지 않는다. DateTimePicker를 직접 쓰는 이상
// 이 import가 없으면 달력 UI에 스타일이 전혀 적용되지 않는다.
// 엔트리(build.tsx → AppProvider)에서 import해야 manifest의 build.html.css에 합쳐진다.
// 모달 컴포넌트에서 import하면 lazy 청크의 별도 CSS로 빠져 호스트가 링크하지 못한다.
import '@mantine/dates/styles.css';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 0,
      refetchOnReconnect: false,
      refetchOnWindowFocus: false,
      useErrorBoundary: false,
      suspense: false,
    },
  },
});

const hackleClient = createInstance(HACKLE_BROWSER_SDK_KEY, {
  exposureEventDedupIntervalMillis: 1_000,
  pollingIntervalMillis: 30_000,
});

export default function AppProvider({ children }: { children: ReactNode }) {
  const user = {
    id: Cookies.get('deviceId'),
  };

  return (
    <QueryClientProvider client={queryClient}>
      <HackleProvider hackleClient={hackleClient} user={user}>
        <ThemeProvider cdnLink={CDN_URL}>
          <NotificationsProvider />
          <ModalsProvider>
            <Global styles={[globals]} />
            {children}
          </ModalsProvider>
        </ThemeProvider>
      </HackleProvider>
    </QueryClientProvider>
  );
}
